import SearchIcon from "@mui/icons-material/Search";
import { Grid, Typography, InputAdornment, TableContainer, TableHead, TableCell, Paper, Table, TableRow, TableBody, TextField, Checkbox } from "@mui/material";

import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isValid, parse } from "date-fns";
import React, { ChangeEvent } from "react";
import FormModal from "../components/FormModal";
import { FormatDataByTypeCode, evalDisableRules, setPageVariables, parseBoolean, emptyFormObject, getFormLineById, parseIntFromTd, parseTimeDurationFromUser, validateEmail, validatePhone, cloneObject } from "../components/FormScripts";
import LUTModal, { fetchLutPickList, refreshDependentRadioLuts } from "../components/LUTModal";
import RadioLutList from "../components/RadioLutList";
import WrappedButton from "../components/WrappedButton";
import { isNullOrUndefined } from "../Util";
import { AssessmentRender } from "./Assessments";
import { FormLine } from "./FormLine";
import SignatureLine from "./SignatureLine";
import MemoControl from "./MemoControl";
import DocumentUploadLine from "./DocumentUploadLine";
import TimeControl from "../components/TimeControl";
import { isFormLineRenderedVisible, useFormClientOptions } from "../FormClientContext";

interface RenderProps {
	formLine: FormLine;
}
export function FormLineRender(props: RenderProps) {
	const fl = props.formLine;
	const formClientOptions = useFormClientOptions();
	const [modified, setModified] = React.useState(false);
	const [val, setVal] = React.useState((fl.lutValue?.value ?? fl.value) as any);
	const [showLutModal, setShowLutModal] = React.useState(false);
	const [formattedVal, setFormattedVal] = React.useState("");
	const [showSubformModal, setShowSubformModal] = React.useState(false);
	const [formModalObj, setFormModalObj] = React.useState(null as any);
	const [disabled, setDisabled] = React.useState(false);
	const [dflChecked, setDflChecked] = React.useState([] as Array<boolean>);
	const [visible, setVisible] = React.useState(fl.isVisible);
	const [required, setRequired] = React.useState(fl.isRequired)

	const [isDrivingFormLine, setIsDrivingFormLine] = React.useState(false);
	const [datePickerRevision, setDatePickerRevision] = React.useState(0);
	const dateInputDirtyRef = React.useRef(false);
	const pendingDateRef = React.useRef<any>(null);
	const acceptDateChangesRef = React.useRef(true);

	fl.toggleVisibility = setVisible;
	fl.toggleRequired = setRequired;

	React.useEffect(() => {
		const timer = window.setTimeout(() => {
			acceptDateChangesRef.current = true;
		}, 0);
		return () => window.clearTimeout(timer);
	}, [datePickerRevision]);

	const normalizeDateModelValue = (value: any, typeCode: string) => {
		if (isNullOrUndefined(value) || value === "") return "";
		const formatted = FormatDataByTypeCode(value, typeCode);
		return formatted || "";
	};

	const parseTypedDateValue = (raw: string) => {
		const trimmed = raw?.trim();
		if (!trimmed) return null;

		const parsed = parse(trimmed, "MM/dd/yyyy", new Date());
		if (isValid(parsed)) return parsed;

		const fallback = new Date(trimmed);
		return fallback.toString().includes("Invalid") ? null : fallback;
	};

	const resolveDateFromBlur = (inputValue: string) => {
		return parseTypedDateValue(inputValue) ?? pendingDateRef.current;
	};

	const toDatePickerDisplayValue = (modelValue: string) => {
		if (!modelValue) return null;
		const parsed = new Date(modelValue);
		return parsed.toString().includes("Invalid") ? modelValue : parsed;
	};

	const applyDateModelValue = (modelValue: string, remountPicker = false) => {
		fl.value = modelValue;
		setFormattedVal(modelValue);
		pendingDateRef.current = null;
		dateInputDirtyRef.current = false;
		if (remountPicker || !modelValue) {
			acceptDateChangesRef.current = false;
			setDatePickerRevision((revision) => revision + 1);
		}
		setVal(toDatePickerDisplayValue(modelValue));
	};

	const forceSetDateValue = (value: any) => {
		const modelValue = normalizeDateModelValue(value, fl.typeCode);
		applyDateModelValue(modelValue, true);
		fl.isDirty = fl.isModified();
	};

	const syncFormLineDisplay = () => {
		const raw = fl.lutValue?.value ?? fl.value;
		if (fl.typeCode === "D" || fl.typeCode === "DT") {
			const modelValue = normalizeDateModelValue(raw, fl.typeCode);
			if (modelValue === normalizeDateModelValue(fl.value, fl.typeCode)) {
				return;
			}
			applyDateModelValue(modelValue, !modelValue);
		} else {
			setVal(raw);
		}
	};

	fl.forceSetValue = fl.typeCode === "D" || fl.typeCode === "DT" ? forceSetDateValue : setVal;

	// eslint-disable-next-line
	React.useEffect(() => {
		if (!isNullOrUndefined(fl.defaultValue) && (window as any).currentForm.formMode === "ADD") {
			try {
				// eslint-disable-next-line
				var res = "";

				// eslint-disable-next-line
				if (fl.typeCode !== "LABEL") res = eval(fl.defaultValue.toString());
				else res = fl.defaultValue.toString();

				fl.setValue(res);
			} catch (e: any) {
				alert("Failed to eval default value for form line: " + fl.caption + "; " + fl.defaultValue + " exception: " + e.toString());
			}
		}
		setFormattedVal(FormatDataByTypeCode(fl.value, fl.typeCode));
		// eslint-disable-next-line
	}, []);

	//#region misc form line functions
	const checkChange = async () => {
		fl.modified = fl.isModified();
		setModified(fl.modified);
		fl.isDirty = fl.modified;
		setFormattedVal("");

		try {
			// eslint-disable-next-line
			if (fl.onchangeEvent) eval(fl.onchangeEvent);
		} catch (ex: any) {
			alert("Error in onchange event for: " + fl.caption);
			console.error(ex);
		}

		syncFormLineDisplay();
		evalDisableRules();
		refreshDependentRadioLuts(fl);
	};

	const commitDateValue = (date: any, typeCode: "D" | "DT") => {
		const modelValue = normalizeDateModelValue(date, typeCode);
		fl.value = modelValue;
		setVal(toDatePickerDisplayValue(modelValue));
		checkChange();
	};

	const handleDatePickerChange = (date: any, typeCode: "D" | "DT") => {
		if (!acceptDateChangesRef.current) {
			return;
		}
		pendingDateRef.current = date;
		if (dateInputDirtyRef.current) {
			return;
		}
		const modelValue = normalizeDateModelValue(date, typeCode);
		if (modelValue === normalizeDateModelValue(fl.value, typeCode)) {
			return;
		}
		commitDateValue(date, typeCode);
	};

	const datePickerKey = `${fl.formLinesId ?? fl.columnName}-${datePickerRevision}`;

	const renderDatePickerInput = (params: any, typeCode: "D" | "DT", textFieldSx: any) => (
		<TextField
			fullWidth={true}
			sx={textFieldSx}
			{...params}
			inputProps={{
				...params.inputProps,
				onInput: () => {
					dateInputDirtyRef.current = true;
				},
			}}
			onBlur={(e) => {
				params.onBlur?.(e);
				if (!dateInputDirtyRef.current) return;

				dateInputDirtyRef.current = false;
				const raw = (e.target as HTMLInputElement).value ?? "";
				const date = resolveDateFromBlur(raw);
				const modelValue = normalizeDateModelValue(date, typeCode);
				if (modelValue !== normalizeDateModelValue(fl.value, typeCode)) {
					commitDateValue(date, typeCode);
				}
			}}
		/>
	);

	const handleLutClose = (res: any) => {
		setShowLutModal(false);
		if (isNullOrUndefined(res)) return;
		else if (!res) {
			res = {
				code_field: null,
				display_exp: null,
				prompt_field: null,
			};
		}

		(fl.lutValue as any).value = res.code_field;
		(fl.lutValue as any).description = res.display_exp;
		(fl.lutValue as any).prompt = res.prompt_field;
		checkChange();
	};

	const launchLutModal = () => {
		setShowLutModal(true);
	};

	const handleSubFormClose = (res: any) => {
		if (!res && !window.confirm("Any unsaved changes will be lost, continue?")) {
			return;
		}
		setShowSubformModal(false);
		(window as any).currentForm = (window as any).parentForm;
		setPageVariables((window as any).currentForm);

		if (!res) {
			if (isDrivingFormLine) {
				const idx = fl.sfValue.findIndex((x: any) => x.keyValue === formModalObj.keyValue);
				fl.sfValue[idx].formMode = "NEW";
				dflChecked[idx] = false;
				setDflChecked(dflChecked);
			}

			return;
		}

		fl.subFormHasData = true;

		if (res.formMode === "NEW" || (res.formMode === "ADD" && res.keyValue.toString().toUpperCase() === "NEW")) {
			res.formMode = "ADD";
			res.keyValue = "NEW" + Math.random().toString().substring(2);
		}

		let existing = fl.sfValue.findIndex((x: any) => x.keyValue === res.keyValue);
		if (existing >= 0) {
			fl.sfValue[existing] = res;
		} else {
			fl.sfValue.push(res);
		}

		fl.setValue(fl.sfValue);
		fl.emptyForm = emptyFormObject(cloneObject(fl.sfValue[0]));
		fl.emptyForm.formMode = "NEW";
	};

	fl.setDisabled = setDisabled;
	fl.getDisabled = () => {
		return disabled;
	};
	if (fl.caption === "Tasks/Schedules" && fl.isGroupHeader) {
		fl.isVisible = false;
	} else if (fl.isVisible && parseBoolean(fl.XMLElementName)) {
		fl.isVisible = false;
	}

	if (fl.isSubForm() && isNullOrUndefined(fl.emptyForm)) {
		fl.emptyForm = emptyFormObject(fl.sfValue[0]);
		fl.emptyForm.formMode = "NEW";
		const cf = (window as any).currentForm;

		if (cf.formMode === "ADD" || cf.formMode === "NEW") {
			fl.sfValue = [];
		}

		if (fl.emptyForm.drivingFormLineId) {
			setIsDrivingFormLine(true);
			const dfl = getFormLineById(fl.emptyForm.drivingFormLineId, fl.emptyForm) as any;
			if (dfl) {
				fetchLutPickList(dfl.lutValue, {
					eventGroup: fl.eventGroupsId,
					eventCategory: fl.eventCategoryId,
				}).then((x) => {
						const search = x.map((x: any) => x.code_field.toLowerCase()).join(",");
						fl.sfValue = fl.sfValue.filter((x: any) => x.formMode === "EDIT" && search.includes(getFormLineById(fl.emptyForm.drivingFormLineId, x).lutValue.value?.toLowerCase()));
						x = x.filter((y: any) => isNullOrUndefined(fl.sfValue.find((z: any) => getFormLineById(fl.emptyForm.drivingFormLineId, z).lutValue.value?.toLowerCase() === y.code_field.toLowerCase())));
						const forms = x.map((form: any, idx: number) => {
							const y = emptyFormObject(fl.emptyForm);
							y.formMode = "NEW";
							y.keyValue = "NEW" + Math.random().toString().substring(3);
							const f = getFormLineById(fl.emptyForm.drivingFormLineId, y) as any;
							f.isDirty = true;
							f.lutValue.description = form.display_exp;
							f.lutValue.prompt = form.prompt_field;
							f.lutValue.value = form.code_field;
							f.disableRule = "true";
							f.isDisabled = true;

							return y;
						});

						fl.sfValue = fl.sfValue.concat(forms);
						fl.sfValue.forEach((y: any, idx: number) => (dflChecked[idx] = y.formMode === "EDIT"));
						fl.setValue(fl.sfValue);
						setDflChecked(dflChecked);
					});
			}
		}
	}

	if (!visible) {
		if (fl.isGroupHeader) {
			(window as any).currentForm.FormLines.filter((x: any) => x.belongsTo?.toLowerCase() === fl.formLinesId.toLowerCase()).forEach((x: any) => {
				x.hide();
				x.isRequired = false;
				if(x.toggleRequired) x.toggleRequired(false)
			});
		}
		return <></>;
	}

	if (!isFormLineRenderedVisible(visible, disabled, formClientOptions)) {
		return <></>;
	}

	if (fl.isGroupHeader)
		return (
			<Grid item xs={12} sx={{ borderBottom: "1px solid #8f8f8f", paddingLeft: 1, paddingTop: 1, paddingBottom: 1, marginBottom: 2 }}>
				<Typography variant="h6">{fl.caption}</Typography>
			</Grid>
		);
	else if (fl.typeCode === "DIVLINE") {
		return (
			<Grid item xs={12} sx={{ borderBottom: "1px solid #8f8f8f", paddingLeft: 1, paddingTop: 1, paddingBottom: 1, marginBottom: 2 }}>
				<Typography variant="h6" sx={{ fontSize: "1.1rem" }}>
					{fl.caption}
				</Typography>
			</Grid>
		);
	}

	const extraProps = { required: required } as any;
	if (modified) extraProps["focused"] = true;

	const gridProps = {
		xs: 12,
		sm: 6,
		md: 4,
		lg: 3,
	};

	if (fl.typeCode === "FK" && !fl.renderLutAsRadioButton) {
		if (fl.isModifiable && !disabled)
			extraProps["InputProps"] = {
				endAdornment: (
					<InputAdornment position="start" disablePointerEvents={!fl.isModifiable || disabled}>
						<SearchIcon
							sx={{ cursor: "pointer" }}
							onClick={() => {
								launchLutModal();
							}}
						/>
					</InputAdornment>
				),
				disabled: true,
			};
	} else if (["RSF", "M", "REMARKS", "SF", "ESF", "TESTS"].includes(fl.typeCode)) {
		extraProps["multiline"] = true;
		gridProps.lg = 12;
		gridProps.md = 12;
		gridProps.sm = 12;
	}

	let placeHolder = "";
	switch (fl.typeCode) {
		case "TD":
			placeHolder = "HH:MM";
			break;
		case "D":
			placeHolder = "MM/DD/YYYY";
			break;
		case "DT":
			placeHolder = "MM/DD/YYYY HH:MM AM";
			break;
	}

	let inputValue = val ?? "";
	if (fl.typeCode === "FK" && fl.lutValue?.value) inputValue = (fl.lutValue?.prompt ? fl.lutValue.prompt + " - " : "") + fl.lutValue?.description;

	inputValue = isNullOrUndefined(formattedVal) ? inputValue : formattedVal;

	const captionComponent = (
		<>
			<Typography color={modified ? "primary" : ""} variant="subtitle1" display={fl.typeCode !== "SF" ? "block" : ""}>
				{fl.caption + (required ? "*" : "")}
			</Typography>
			{fl.isSubForm() && fl.isModifiable && !isDrivingFormLine && (
				<WrappedButton
					size="small"
					caption="Add Row"
					sx={{ float: "right" }}
					onClick={() => {
						setShowSubformModal(true);
						setFormModalObj(fl.emptyForm);
					}}></WrappedButton>
			)}
		</>
	);
	//#endregion

	switch (fl.typeCode) {
		case "SF":
		case "ESF":
			let sfGridLines = fl.emptyForm.FormLines.filter((x: any) => x.isVisible && x.isSubformVisible && !["ESF", "SF", "RSF", "TESTS", "DIVLINE"].includes(x.typeCode) && !x.isGroupHeader);

			return (
				<>
					<FormModal open={showSubformModal} onClose={handleSubFormClose} form={formModalObj} isSubForm={true}></FormModal>
					<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
						{captionComponent}
						<TableContainer component={Paper}>
							<Table size="small">
								<TableHead>
									<TableRow>
										{isDrivingFormLine && <TableCell></TableCell>}
										{sfGridLines.map((x: any, idx: number) => {
											return <TableCell key={idx}>{x.caption}</TableCell>;
										})}
									</TableRow>
								</TableHead>
								<TableBody>
									{fl.sfValue.map((x: any, idx: number) => {
										return (
											<TableRow
												hover
												key={idx}
												sx={{ cursor: "pointer", border: dflChecked[idx] ? "4px solid #78dfff" : "initial" }}
												onClick={() => {
													if (dflChecked[idx] || !isDrivingFormLine) {
														setShowSubformModal(true);
														setFormModalObj(x);
													}
												}}>
												{isDrivingFormLine && (
													<TableCell key={-1 * (idx + 1)} sx={{ padding: 0, verticalAlign: "top" }}>
														<Checkbox
															checked={dflChecked[idx]}
															disabled={!fl.isModifiable}
															onClick={(event) => {
																// event.preventDefault();
																event.stopPropagation();
																const c = (event.target as any).checked;
																const d = cloneObject(dflChecked);
																d[idx] = c;
																x.formMode = c ? "ADD" : "NEW";
																setDflChecked(d);

																if (c && x.FormLines.filter((y: any) => y.isVisible && y.isModifiable && y.formLinesId && y.formLinesId.toLowerCase() !== x.drivingFormLineId.toLowerCase()).length > 0) {
																	setShowSubformModal(true);
																	setFormModalObj(x);
																} else {
																	setShowSubformModal(false);
																	setFormModalObj(null);
																}

																return false;
															}}
														/>
													</TableCell>
												)}
												{sfGridLines.map((col: any, idx: number) => {
													return (
														<TableCell sx={{ verticalAlign: "top" }} key={idx}>
															{x.getFormDisplay(col.columnName)}
														</TableCell>
													);
												})}
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</Grid>
				</>
			);
		case "RSF":
			return (
				<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
					{captionComponent}
					<TableContainer component={Paper}>
						<Table size="small">
							<TableHead>
								<TableRow>
									{fl.srValue?.columns
										.filter((x) => x.is_visible)
										.map((x) => {
											return <TableCell key={x.column_name}>{x.caption}</TableCell>;
										})}
								</TableRow>
							</TableHead>
							<TableBody>
								{Array.isArray(val) &&
									val.map((x) => {
										return (
											<TableRow>
												{fl.srValue?.columns
													.filter((x) => x.is_visible)
													.map((col) => {
														return <TableCell>{FormatDataByTypeCode(x[col.column_name], col.type_code)}</TableCell>;
													})}
											</TableRow>
										);
									})}
							</TableBody>
						</Table>
					</TableContainer>
				</Grid>
			);
		case "M":
			return (
				<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
					<MemoControl isDisabled={disabled} fl={fl} captionComponent={captionComponent} checkChange={checkChange} val={val} />
				</Grid>
			);
		case "D":
			return (
				<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
					<LocalizationProvider dateAdapter={AdapterDateFns} sx={{ width: 1 }}>
						<DesktopDatePicker
							key={datePickerKey}
							{...extraProps}
							inputFormat="MM/dd/yyyy"
							disabled={!fl.isModifiable || disabled}
							label={fl.caption + (required ? " *" : "")}
							value={val}
							onChange={(date: any) => handleDatePickerChange(date, "D")}
							renderInput={(params: any) => renderDatePickerInput(params, "D", { fontSize: "50px" })}></DesktopDatePicker>
					</LocalizationProvider>
				</Grid>
			);
		case "DT":
			return (
				<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1, display: "flex" }}>
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DesktopDatePicker
							key={datePickerKey}
							{...extraProps}
							inputFormat="MM/dd/yyyy"
							disabled={!fl.isModifiable || disabled}
							label={fl.caption + (required ? " *" : "")}
							value={val}
							onChange={(date: any) => handleDatePickerChange(date, "DT")}
							renderInput={(params: any) => renderDatePickerInput(params, "DT", { fontSize: "50px", flex: 1, marginRight: "4px" })}></DesktopDatePicker>
					</LocalizationProvider>
					<TimeControl
						updateFormVal={(newTime: any) => {
							const newVal = FormatDataByTypeCode(val, "D") + " " + newTime;
							fl.value = newVal;
							setFormattedVal(fl.value);
							setVal(fl.value);
							checkChange();
						}}
						val={val}
						disabled={!fl.isModifiable || disabled}
					/>
				</Grid>
			);
		case "L":
			return (
				<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
					{captionComponent}
					<Checkbox
						onChange={(x: ChangeEvent) => {
							fl.value = (x.target as HTMLInputElement).checked;
							checkChange();
						}}
						checked={fl.value || false}
						value={fl.value || false}
						disabled={disabled || !fl.isModifiable}
						sx={{ paddingTop: 0, paddingLeft: 0 }}></Checkbox>
				</Grid>
			);
		case "TESTS":
			return (
				<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
					{captionComponent}
					<AssessmentRender fl={fl}></AssessmentRender>
				</Grid>
			);
		case "ESIGN":
			return (
				<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
					{captionComponent}
					<SignatureLine onChange={checkChange} fl={fl}></SignatureLine>
				</Grid>
			);
		case "LABEL":
			return (
				<Grid item xs={12} sx={{ marginBottom: 2, paddingLeft: 2, paddingRight: 2, boxSizing: "border-box" }}>
					<div
						dangerouslySetInnerHTML={{ __html: fl.defaultValue }}
						style={{ maxWidth: "100%", overflowWrap: "break-word" }}
					/>
				</Grid>
			);
		case "DOC":
		case "DOCFILE":
		case "IMGFILE":
		case "IMG":
			// Document/image upload type codes - supports desktop and mobile file selection
			return (
				<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
					{captionComponent}
					<DocumentUploadLine onChange={checkChange} fl={fl}></DocumentUploadLine>
				</Grid>
			);
		case "FK":
			if (fl.renderLutAsRadioButton) {
				return (
					<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
						{captionComponent}
						<RadioLutList fl={fl} checkChange={checkChange} disabled={disabled} />
					</Grid>
				);
			}
			return (
				<>
					<LUTModal onClose={handleLutClose} open={showLutModal} lutValue={fl.lutValue}></LUTModal>
					<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
						<TextField
							disabled={!fl.isModifiable || disabled}
							color="primary"
							{...extraProps}
							onChange={(x: ChangeEvent) => {
								fl.value = (x.target as HTMLInputElement).value;
								checkChange();
							}}
							onBlur={() => {
								setVal(fl.value);
								setFormattedVal(FormatDataByTypeCode(fl.value, fl.typeCode));
							}}
							placeholder={placeHolder}
							label={fl.caption}
							value={inputValue}
							sx={{ width: "100%" }}
						/>
					</Grid>
				</>
			);
		default:
			return (
				<Grid item {...gridProps} sx={{ marginBottom: 2, paddingLeft: 1, paddingRight: 1 }}>
					<TextField
						disabled={!fl.isModifiable || disabled}
						color="primary"
						{...extraProps}
						onChange={(x: ChangeEvent) => {
							fl.value = (x.target as HTMLInputElement).value;
							checkChange();
						}}
						onBlur={() => {
							switch (fl.typeCode) {
								case "TD":
									fl.value = parseTimeDurationFromUser(fl.value);
									fl.value = parseIntFromTd(fl.value);
									break;
								case "EMAIL":
									fl.value = validateEmail(fl.value);
									break;
								case "T":
									fl.value = validatePhone(fl.value, fl.caption);
									break;
							}
							setVal(fl.value);
							setFormattedVal(FormatDataByTypeCode(fl.value, fl.typeCode));
						}}
						placeholder={placeHolder}
						label={fl.caption}
						value={inputValue}
						sx={{ width: "100%" }}
					/>
				</Grid>
			);
	}
}
